<template>
    <div>
        <v-card width="800" variant="outlined" class="mx-auto">
            <v-card-title>Upload Your Lesson Data</v-card-title>
            <v-card-item>
                <v-card-subtitle>Lesson Date</v-card-subtitle>
                <v-menu v-model="menu" :close-on-content-click="false" :nudge-right="40" transition="scale-transition"
                    offset-y min-width="auto">
                    <template v-slot:activator="{ props }">
                        <v-text-field v-model="formattedDate" label="Select Date" prepend-icon="mdi-calendar" readonly
                            v-bind="props" />
                    </template>
                    <v-date-picker v-model="selectedDate" no-title scrollable>
                    </v-date-picker>
                </v-menu>
            </v-card-item>
            <v-card-item>
                <v-card-subtitle>Recorded Data</v-card-subtitle>
                <v-file-input v-model="audioFile" accept="audio/*" placeholder="Pick an audio file"
                    prepend-icon="mdi-music" label="Audio File"></v-file-input>
            </v-card-item>
            <v-card-item>
                <v-card-subtitle>Note</v-card-subtitle>
                <v-text-field v-model="note" label="Note" hide-details="auto" prepend-icon="mdi-pencil"></v-text-field>
            </v-card-item>
            <v-card-item>
                <v-btn @click="uploadFile" text="Upload" flat />
            </v-card-item>
        </v-card>
        <!-- Loading dialog -->
        <v-dialog v-model="uploading" persistent max-width="300">
            <v-card>
                <v-card-text class="text-center">
                    <v-progress-circular indeterminate size="64"></v-progress-circular>
                    <div>Uploading...</div>
                </v-card-text>
            </v-card>
        </v-dialog>
        <!-- Completion dialog -->
        <v-dialog v-model="uploadComplete" max-width="300">
            <v-card>
                <v-card-title class="text=h5">Upload Complete</v-card-title>
                <v-card-text>
                    Your audio file has been uploaded successfully!
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" @click="executeTranscribe">Transcribe</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <!-- Transcribing in progress dialog -->
        <v-dialog v-model="processing" persistent max-width="300">
            <v-card>
                <v-card-text class="text-center">
                    <v-progress-circular indeterminate size="64" />
                    <div>Transcribing...<br>*It takes around 1-2 mins...</div>
                </v-card-text>
            </v-card>
        </v-dialog>

        <!-- Complete transcribing dialog -->
        <v-dialog v-model="processingComplete" max-width="300">
            <v-card>
                <v-card-title class="text-h5">Processing Complete</v-card-title>
                <v-card-text>
                    Your lesson has been transcribed successfully!
                </v-card-text>
                <v-card-actions>
                    <v-btn color="primary" @click="processingComplete = false">OK</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Upload Canceled Dialog -->
        <v-dialog v-model="isCancel" persistent max-width="300">
            <v-card>
                <v-card-text class="text-center">
                    <div>Uploaded file must be audio file and less than 25MB.</div>
                </v-card-text>
                <v-card-actions>
                    <v-btn color="primary" @click="isCancel = false">OK</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Failed dialog -->
        <v-dialog v-model="processingFailed" max-width="300">
            <v-card>
                <v-card-title class="text-h5">Proccesing Failed.</v-card-title>
                <v-card-text>
                    Sorry, we couldn't process your request. <br>Please try again later.
                </v-card-text>
                <v-card-actions>
                    <v-btn color="primary" @click="processingFailed = false">OK</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Exceeded upload num dialog -->
        <v-dialog v-model="exceededUploadNum" max-width="400">
            <v-card>
                <v-card-title class="text-h5">Exceeded Upload Times</v-card-title>
                <v-card-text>
                    You are allowed to upload the file three times per day.
                </v-card-text>
                <v-card-actions>
                    <v-btn color="primary" @click="exceededUploadNum = false">OK</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, computed } from 'vue';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import moment from 'moment';
import axios from 'axios';
import store from '@/store';

import { auth, storage } from '@/plugins/firebase';
import { Profile } from '@/models/models'

export default defineComponent({
    name: 'AudioUpload',
    setup() {
        const selectedDate = ref<Date>(new Date());
        const note = ref<string>("")
        const currentUser = auth.currentUser;
        const audioFile = ref<File | null>(null);
        const downloadUrl = ref<string>("");
        const profile = ref<Profile |null>(null);
        const exceededUploadNum = ref(false);

        const menu = ref(false);
        const uploading = ref(false);
        const uploadComplete = ref(false);
        const processing = ref(false);
        const processingComplete = ref(false);
        const processingFailed = ref(false);
        const isCancel = ref(false);

        const rules = [
            (value: File | null) => {
                if (!value) {
                    return true; // No file selected, no error should be shown
                }   
                return value.size < 25000000 || 'Audio file size should be less than 25 MB!';
            },
        ];

        onMounted(async () => {
            if (currentUser) {
                await store.dispatch('profile/fetchProfile', currentUser.uid);
                profile.value = store.getters['profile/profile'];

                resetCount();
            }
        })

        const formattedDate = computed(() => {
            return moment(selectedDate.value).format('YYYY/MM/DD');
        });

        const resetCount = async () => {
            const today = moment().startOf('day');
            if (profile.value?.last_uploaded_date) {
                const lastUploaded = moment(profile.value.last_uploaded_date).startOf('day');
                if (lastUploaded.isBefore(today)) {
                    profile.value.transcribed_count_per_day = 0;
                    profile.value.suggested_count_per_day = 0;
                    profile.value.uploaded_files_count_per_day = 0;
                    profile.value.last_uploaded_date = today.toDate();
                    await store.dispatch('profile/updateProfile', { uid: currentUser?.uid, profile: profile.value });
                }
            }
        }

        const uploadFile = async () => {
            if (!audioFile.value) return;

            if (profile.value?.membership_plan === 'basic' && profile.value.transcribed_count_per_day > 2) {
                exceededUploadNum.value = true;
                return;
            }

            if (audioFile.value) {
                const fileType = audioFile.value.type.startsWith('audio/') || audioFile.value.type.startsWith('video/');
                const fileSize = audioFile.value.size <= 25 * 1024 * 1024; // 25MB in bytes

                if (!fileType || !fileSize) {
                    isCancel.value = true;
                    return; // Disable upload if the file is not audio or larger than 25MB
                }
            }
            uploading.value = true; // Show loading dialog

            try {
                const modifiedFile = new Blob([audioFile.value], { type: 'audio/webm' });

                const fileRef = storageRef(storage, `uploads/${currentUser?.uid}/${audioFile.value.name}`);
                await uploadBytes(fileRef, modifiedFile);
                downloadUrl.value = await getDownloadURL(fileRef);
                uploading.value = false;
                uploadComplete.value = true;

                if (profile.value) {
                    profile.value.last_uploaded_date = new Date();
                    profile.value.uploaded_files_count_per_day += 1;
                    await store.dispatch('profile/updateProfile', { uid: currentUser?.uid, profile: profile.value })
                } else {
                    console.error("Profile is not defined.")
                }
            } catch (error) {
                uploading.value = false;
                processingFailed.value = true;
                console.error("Upload failed:", error);
            }
        };

        const executeTranscribe = async () => {
            if (!audioFile.value) return;
            uploadComplete.value = false;
            processing.value = true;

            try {
                const fileName = audioFile.value.name;
                const response = await axios.post(process.env.VUE_APP_CLOUD_RUN_URL + 'audio',
                    {
                        "gcs_uri": "gs://ksst-genai-app.appspot.com/uploads/" + currentUser?.uid + "/" + fileName,
                        "user_uid": currentUser?.uid,
                        "lesson_date": selectedDate.value.toDateString(),
                        "note": note.value,
                        "download_url": downloadUrl.value
                    }
                )

                processing.value = false;
                processingComplete.value = true;

                if (profile.value) {
                    profile.value.transcribed_count_per_day += 1;
                    profile.value.last_uploaded_date = new Date();
                    await store.dispatch('profile/updateProfile', { uid: currentUser?.uid, profile: profile.value })
                } else {
                    console.error("Profile is not defined.")
                }

                console.log("Cloud Run response:", response.data);
                resetFileInput();
            } catch (error) {
                processing.value = false;
                processingFailed.value = true;
                console.error("Cloud Run process failed:", error);
            }
        }

        const resetFileInput = () => {
            note.value = "";
            audioFile.value = null;
        }

        return {
            menu,
            note,
            selectedDate,
            audioFile,
            uploading,
            uploadComplete,
            rules,
            processing,
            processingComplete,
            processingFailed,
            exceededUploadNum,
            isCancel,
            formattedDate,
            uploadFile,
            executeTranscribe
        }
    },
});
</script>

<style lang="scss" scoped>
@import '@/assets/sass/size.scss';
</style>
